// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import wrapInContainerStyles from "@insite/client-framework/Common/wrapInContainerStyles";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import { headerText } from "@turnerBlueprint/App/SharedStyles/AutoCompleteSharedStyles";
import { createHorizontalSmallHeadingAndTextStyles } from "@turnerBlueprint/App/SharedStyles/smallHeadingAndTextStyles";
import { autocompleteProductsStyles } from "@turnerBlueprint/Overrides/Widgets/Header/AutocompleteProducts";
import IconArrowRight from "@turnerBlueprint/XDL/Icons/IconArrowRight";
import { fontConfig } from "@turnerBlueprint/XDL/PreStyle/typography";
import { color, createLinkIconStyles, ellipsis, mergeWithCss, xScreen } from "@turnerBlueprint/XDL/Utilities";
import { css } from "styled-components";

autocompleteProductsStyles.header = {
    css: css`
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: 8px;
    `,
};

autocompleteProductsStyles.headerText = {
    ...mergeWithCss(headerText, {
        css: css`
            margin: 0;
        `,
    }),
};

autocompleteProductsStyles.headerLink = {
    ...createLinkIconStyles(
        IconArrowRight,
        undefined,
        undefined,
        {
            src: IconArrowRight,
            css: css`
                &,
                & svg {
                    width: 5px;
                    height: auto;
                }
            `,
        },
        "right",
    ),
};
autocompleteProductsStyles.container = {
    css: css`
        display: flex;
        column-gap: 8px;
        &:not(:last-of-type) {
            margin-bottom: 16px;
        }
        ${xScreen("lte", "sm")} {
            column-gap: 12px;
            flex-wrap: wrap;
        }
    `,
};

autocompleteProductsStyles.focusedContainer = {
    ...mergeWithCss(autocompleteProductsStyles.container, {
        css: css`
            background-color: ${color("common.ui8", 0.3)};
        `,
    }),
};

autocompleteProductsStyles.imageClickable = {
    css: css`
        max-width: 52px;
        max-height: 52px;
        flex-basis: 52px;
    `,
};

autocompleteProductsStyles.image = {
    css: css`
        width: 52px;
        min-width: 52px;
        height: 52px;
        cursor: pointer;
        object-fit: contain;
    `,
};
autocompleteProductsStyles.infoGridItem = {
    css: css`
        display: flex;
        flex-direction: column;
        flex: 1;
        padding-right: 4px;
        ${xScreen("lte", "sm")} {
            padding: 0;
            flex: 1 0 auto;
            width: calc(100% - 67px);
        }
    `,
};

autocompleteProductsStyles.actionsGridItem = {
    css: css`
        display: flex;
        flex-direction: column;
        flex: 0 0 145px;
        row-gap: 14px;
        ${xScreen("lte", "sm")} {
            margin-left: 67px;
            flex-grow: 1;
            row-gap: 12px;
        }
    `,
};

autocompleteProductsStyles.titleLink = {
    color: "text.main",
    typographyProps: {
        size: 12,
        weight: fontConfig.weight.bold,
        color: "text.main",
        css: css`
            letter-spacing: 0;
            line-height: 13px;
            vertical-align: unset;
            ${wrapInContainerStyles}
            ${ellipsis(2)};
            ${xScreen("lte", "md")} {
                ${ellipsis(3)}
            }
        `,
    },
    css: css`
        width: auto;
        margin-bottom: 8px;
        align-items: flex-start;
        -webkit-box-align: start;

        &:hover {
            ${TypographyStyle} {
                color: ${color("common.ui9")};
            }
        }
    `,
};

autocompleteProductsStyles.productNumbersContainer = {
    css: css`
        display: flex;
        row-gap: 4px;
        flex-direction: column;
        ${xScreen("lte", "sm")} {
            margin-bottom: 12px;
        }
    `,
};

autocompleteProductsStyles.erpNumberText = {
    ...createHorizontalSmallHeadingAndTextStyles(),
};

autocompleteProductsStyles.actions = {
    addToCartContainer: {
        css: css`
            min-width: 0;
            flex-wrap: nowrap;
            gap: 12px;
            margin-top: auto;
        `,
    },
    productAddToCartButtonStyles: {
        sizeVariant: "small",
        typographyProps: {
            size: 11,
            lineHeight: "13px;",
        },
    },
    qtyWrapper: {
        css: css`
            width: 38px;
        `,
    },
    addToCartButtonWrapper: {
        css: css`
            min-width: 97px;
        `,
    },
    qtyField: {
        qtyFieldStyles: {
            wrapper: {
                css: css`
                    width: 38px;
                    min-width: 38px;
                `,
            },
            editableQuantityTextField: {
                cssOverrides: {
                    inputSelect: css`
                        height: 32px;
                    `,
                },
            },
            actionButton: {
                css: css`
                    display: none;
                `,
            },
        },
    },
};

autocompleteProductsStyles.productPriceStyles = {
    price: {
        priceText: {
            size: 14,
            lineHeight: "16px",
        },
        unitOfMeasure: {
            unitOfMeasureText: {
                size: 12,
                lineHeight: "13px",
            },
        },
        errorText: {
            size: 14,
            lineHeight: "16px",
        },
        realTimeText: {
            size: 14,
            lineHeight: "16px",
        },
    },
    quoteMessageWrapper: {
        css: css`
            white-space: nowrap;
        `,
    },
};
